import { LocalDate } from "@js-joda/core";
import { useTranslation } from "react-i18next";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { DatePicker } from "components/designV2/datePicker/datePicker";
import { Dropdown } from "components/designV2/dropdown/dropdown";
import { InputField } from "components/designV2/inputfield/inputfield";
import { SalaryType, WorkerEmploymentType } from "model/Salary";
import type {
  StaffProfileData,
  StaffProfileSelectedEmployment,
} from "services/api/company/staff.service";
import { toEmploymentTypeDescription } from "web-apps/company/components/common/employmentType/employmentType.logic";
import {
  calculateMonthlyPayoutSalary,
  formatMonthlySalary,
} from "web-apps/company/components/common/salary/monthlyPayoutSalary.logic";
import {
  getEmploymentFieldVisibility,
  getVacationFieldVisibility,
} from "web-apps/company/components/staff/components/shared/staffEmploymentFieldVisibility.logic";
import type { EmploymentUiRules } from "web-apps/company/components/staff/components/shared/staffEmploymentRules.logic";
import { StaffEmploymentEndDateField } from "web-apps/company/components/staff/components/shared/staffEmploymentEndDateField.component";
import { EmploymentTypeDescription } from "web-apps/company/components/staff/components/shared/staffEmploymentType.styled";
import { createIntegerInputHandlers } from "web-apps/company/components/staff/components/shared/staffNumericInput.logic";
import { getSelectedOptionLabel } from "web-apps/company/components/staff/components/shared/staffPanel.options.logic";
import {
  FieldCard,
  FieldLabel,
  FieldValue,
  NoEmploymentBanner,
  NoEmploymentBannerBody,
  Section,
  SectionActionRow,
  SectionBox,
  SectionTitle,
  SalaryFieldsGrid,
  TwoColumnFieldsGrid,
  UpcomingEmploymentBanner,
  UpcomingEmploymentBannerBody,
} from "web-apps/company/components/staff/components/profilePanel/layout/profilePanel.styled";
import {
  EmployeeNumberFieldCard,
  RoleFieldCard,
} from "web-apps/company/components/staff/components/profilePanel/domains/employment/profilePanel.employmentSection.styled";
import type {
  EmploymentDetailsForm,
  EmploymentOptions,
} from "web-apps/company/components/staff/components/profilePanel/profilePanel.contract";
import { getEmploymentFieldValidationErrors } from "web-apps/company/components/staff/components/profilePanel/state/profilePanel.state.validation.logic";
import { formatDateLabelInStockholm } from "web-apps/company/utils/timeAndDate";

interface ProfilePanelEmploymentSectionProps {
  readonly selectedEmployment: StaffProfileSelectedEmployment | null;
  readonly editedEmploymentDetails: EmploymentDetailsForm;
  readonly employmentOptions: EmploymentOptions;
  readonly employmentRules: EmploymentUiRules;
  readonly isEditMode: boolean;
  readonly isLoadingEmployeeNumber: boolean;
  readonly isSaving: boolean;
  readonly profile: StaffProfileData;
  readonly showNoEmploymentBanner: boolean;
  readonly upcomingEmploymentStartDate: string | null;
  readonly onChangePaymentMethodClick: () => void;
  readonly onChangeSalaryClick: () => void;
  readonly onChangeTaxClick: () => void;
  readonly onChangeToPermanentClick: () => void;
  readonly onEmploymentFieldChange: <
    TField extends keyof EmploymentDetailsForm,
  >(
    field: TField,
    value: EmploymentDetailsForm[TField],
  ) => void;
  readonly onFetchEmployeeNumber: () => void;
}

export const ProfilePanelEmploymentSection = (
  props: ProfilePanelEmploymentSectionProps,
): JSX.Element => {
  const { t } = useTranslation();
  const toMandatoryLabel = (label: string): string =>
    `${label} (${t("Staff.AddStaffPanelMandatory")})`;
  const salaryTypeInForm = props.isEditMode
    ? props.editedEmploymentDetails.salaryType
    : props.selectedEmployment?.salaryType;
  const hasUpcomingEmployment = props.upcomingEmploymentStartDate != null;
  const paymentMethod = props.profile.paymentMethods[0] ?? null;
  // Field visibility by employment type is applied only while editing; the
  // read-only view keeps showing whatever values exist.
  const fieldVisibility = getEmploymentFieldVisibility(
    props.editedEmploymentDetails.employmentType,
  );
  const employmentTypeDescription = toEmploymentTypeDescription({
    employmentType: props.editedEmploymentDetails.employmentType,
    translate: t,
  });
  const shouldShowWorkPercentage =
    salaryTypeInForm === SalaryType.Monthly &&
    (!props.isEditMode || fieldVisibility.showSalaryType);
  const noticeDaysHandlers = createIntegerInputHandlers(
    props.editedEmploymentDetails.noticeDays,
    (value) => props.onEmploymentFieldChange("noticeDays", value),
  );
  const vacationDaysHandlers = createIntegerInputHandlers(
    props.editedEmploymentDetails.vacationDays,
    (value) => props.onEmploymentFieldChange("vacationDays", value),
  );
  const vacationVisibility = getVacationFieldVisibility(salaryTypeInForm);
  const selectedVacationPayoutLabel =
    props.selectedEmployment?.vacationPayoutType === "Automatic" ||
    props.selectedEmployment?.vacationPayoutType === "Manual"
      ? getSelectedOptionLabel({
          options: props.employmentOptions.vacationPayoutOptions,
          value: props.selectedEmployment.vacationPayoutType,
        })
      : "-";
  const validationErrors = getEmploymentFieldValidationErrors(
    props.editedEmploymentDetails,
  );
  const payoutSalary = calculateMonthlyPayoutSalary({
    salary: props.selectedEmployment?.salary,
    salaryType: props.selectedEmployment?.salaryType,
    workPercentage: props.selectedEmployment?.workPercentage,
  });
  const shouldShowPayoutSalary = payoutSalary != null;

  return (
    <>
      <Section>
        <SectionTitle>{t("Staff.ProfilePanelEmployment")}</SectionTitle>
        {props.upcomingEmploymentStartDate != null && (
          <UpcomingEmploymentBanner>
            <UpcomingEmploymentBannerBody>
              {t("Staff.ProfilePanelUpcomingEmploymentStartsFrom", {
                date: formatDateLabelInStockholm(
                  props.upcomingEmploymentStartDate,
                  "sv-SE",
                ),
              })}
            </UpcomingEmploymentBannerBody>
          </UpcomingEmploymentBanner>
        )}
        {!hasUpcomingEmployment && props.showNoEmploymentBanner && (
          <NoEmploymentBanner>
            <NoEmploymentBannerBody>
              {t("Staff.ProfilePanelNoActiveOrUpcomingEmployment")}
            </NoEmploymentBannerBody>
          </NoEmploymentBanner>
        )}
        <SectionBox>
          <TwoColumnFieldsGrid>
            <RoleFieldCard>
              {props.isEditMode ? (
                <InputField
                  fullWidth
                  label={toMandatoryLabel(t("Staff.AddStaffPanelFieldRole"))}
                  value={props.editedEmploymentDetails.role}
                  hasError={validationErrors.role != null}
                  errorText={
                    validationErrors.role == null
                      ? undefined
                      : t(validationErrors.role.translationKey)
                  }
                  onChange={(role) =>
                    props.onEmploymentFieldChange("role", role)
                  }
                />
              ) : (
                <>
                  <FieldValue>
                    {props.selectedEmployment?.role?.trim() || "-"}
                  </FieldValue>
                  <FieldLabel>{t("Staff.AddStaffPanelFieldRole")}</FieldLabel>
                </>
              )}
            </RoleFieldCard>
            <EmployeeNumberFieldCard>
              {props.isEditMode ? (
                <InputField
                  fullWidth
                  type="number"
                  label={toMandatoryLabel(
                    t("Staff.AddStaffPanelFieldEmployeeNumber"),
                  )}
                  value={props.editedEmploymentDetails.employeeNumber}
                  hasError={validationErrors.employeeNumber != null}
                  errorText={
                    validationErrors.employeeNumber == null
                      ? undefined
                      : t(validationErrors.employeeNumber.translationKey)
                  }
                  iconName="v2/reload"
                  iconSize="18px"
                  iconAriaLabel={t(
                    "Staff.AddStaffPanelActionReloadEmployeeNumber",
                  )}
                  iconTabbable
                  onIconClick={props.onFetchEmployeeNumber}
                  disabled={props.isLoadingEmployeeNumber}
                  onChange={(employeeNumber) =>
                    props.onEmploymentFieldChange(
                      "employeeNumber",
                      employeeNumber,
                    )
                  }
                />
              ) : (
                <>
                  <FieldValue>
                    {props.selectedEmployment?.employeeNumber ?? "-"}
                  </FieldValue>
                  <FieldLabel>
                    {t("Staff.AddStaffPanelFieldEmployeeNumber")}
                  </FieldLabel>
                </>
              )}
            </EmployeeNumberFieldCard>
            <FieldCard>
              {props.isEditMode ? (
                <>
                  <Dropdown
                    fullWidth
                    label={toMandatoryLabel(t("Staff.EmploymentType"))}
                    options={props.employmentOptions.employmentOptions}
                    value={props.editedEmploymentDetails.employmentType}
                    onChange={(employmentType: string) =>
                      props.onEmploymentFieldChange(
                        "employmentType",
                        employmentType,
                      )
                    }
                  />
                  {employmentTypeDescription && (
                    <EmploymentTypeDescription $hasTopMargin>
                      {employmentTypeDescription}
                    </EmploymentTypeDescription>
                  )}
                </>
              ) : (
                <>
                  <FieldValue>
                    {props.selectedEmployment?.employmentType
                      ? t(
                          `Staff.employmentType${props.selectedEmployment.employmentType}`,
                        )
                      : "-"}
                  </FieldValue>
                  <FieldLabel>{t("Staff.EmploymentType")}</FieldLabel>
                </>
              )}
            </FieldCard>
            {(!props.isEditMode || fieldVisibility.showEmployeeCategory) && (
              <FieldCard>
                {props.isEditMode ? (
                  <Dropdown
                    fullWidth
                    label={toMandatoryLabel(
                      t("OrganizationalStructure.EmployeeCategory"),
                    )}
                    options={props.employmentOptions.employeeCategoryOptions}
                    value={props.editedEmploymentDetails.employeeCategory}
                    onChange={(employeeCategory: string) =>
                      props.onEmploymentFieldChange(
                        "employeeCategory",
                        employeeCategory,
                      )
                    }
                  />
                ) : (
                  <>
                    <FieldValue>
                      {props.selectedEmployment?.employeeCategory &&
                      props.selectedEmployment.employeeCategory !== "Undefined"
                        ? (props.employmentOptions.employeeCategoryLabelByValue[
                            props.selectedEmployment.employeeCategory
                          ] ?? props.selectedEmployment.employeeCategory)
                        : "-"}
                    </FieldValue>
                    <FieldLabel>
                      {t("Staff.AddStaffPanelFieldStaffType")}
                    </FieldLabel>
                  </>
                )}
              </FieldCard>
            )}
            <FieldCard>
              {props.isEditMode ? (
                <DatePicker
                  label={toMandatoryLabel(
                    t("Staff.AddStaffPanelFieldStartDate"),
                  )}
                  value={props.editedEmploymentDetails.startDate}
                  allowClear={false}
                  onChange={(startDate: LocalDate | null) =>
                    props.onEmploymentFieldChange("startDate", startDate)
                  }
                />
              ) : (
                <>
                  <FieldValue>
                    {formatDateLabelInStockholm(
                      props.selectedEmployment?.startDate ?? "",
                      "sv-SE",
                    )}
                  </FieldValue>
                  <FieldLabel>
                    {t("Staff.AddStaffPanelFieldStartDate")}
                  </FieldLabel>
                </>
              )}
            </FieldCard>
            <FieldCard>
              {props.isEditMode ? (
                <StaffEmploymentEndDateField
                  allowClear={!props.employmentRules.isEndDateRequiredOnSave}
                  employmentType={props.editedEmploymentDetails.employmentType}
                  endDate={props.editedEmploymentDetails.endDate}
                  label={
                    props.employmentRules.isEndDateRequiredOnSave
                      ? toMandatoryLabel(t("Staff.AddStaffPanelFieldEndDate"))
                      : t("Staff.AddStaffPanelFieldEndDate")
                  }
                  startDate={props.editedEmploymentDetails.startDate}
                  onChange={(endDate: LocalDate | null) =>
                    props.onEmploymentFieldChange("endDate", endDate)
                  }
                />
              ) : (
                <>
                  <FieldValue>
                    {formatDateLabelInStockholm(
                      props.selectedEmployment?.endDate ?? "",
                      "sv-SE",
                    )}
                  </FieldValue>
                  <FieldLabel>
                    {t("Staff.AddStaffPanelFieldEndDate")}
                  </FieldLabel>
                </>
              )}
            </FieldCard>
            {shouldShowWorkPercentage && (
              <FieldCard>
                {props.isEditMode ? (
                  <InputField
                    fullWidth
                    type="number"
                    label={toMandatoryLabel(
                      `${t("Staff.AddStaffPanelFieldWorkingHours")} (%)`,
                    )}
                    value={props.editedEmploymentDetails.workPercentage}
                    hasError={validationErrors.workPercentage != null}
                    errorText={
                      validationErrors.workPercentage == null
                        ? undefined
                        : t(validationErrors.workPercentage.translationKey)
                    }
                    onChange={(workPercentage) =>
                      props.onEmploymentFieldChange(
                        "workPercentage",
                        workPercentage,
                      )
                    }
                  />
                ) : (
                  <>
                    <FieldValue>
                      {props.profile.workPercentage == null
                        ? "-"
                        : `${Math.round(props.profile.workPercentage * 100)}%`}
                    </FieldValue>
                    <FieldLabel>
                      {`${t("Staff.AddStaffPanelFieldWorkingHours")} (%)`}
                    </FieldLabel>
                  </>
                )}
              </FieldCard>
            )}
          </TwoColumnFieldsGrid>
          {!props.isEditMode &&
            props.selectedEmployment?.employmentType ===
              WorkerEmploymentType.INTRODUCTORY && (
              <SectionActionRow>
                <SecondaryButton
                  label={t("Staff.ProfilePanelChangeToPermanentButton")}
                  size="small"
                  disabled={props.isSaving}
                  onClick={props.onChangeToPermanentClick}
                />
              </SectionActionRow>
            )}
        </SectionBox>
      </Section>

      {(!props.isEditMode || fieldVisibility.showSalary) && (
        <Section>
          <SectionTitle>{t("Staff.ProfilePanelSalary")}</SectionTitle>
          <SectionBox>
            <SalaryFieldsGrid $hasPayoutSalary={shouldShowPayoutSalary}>
              <FieldCard>
                <FieldValue>
                  {props.selectedEmployment?.salaryType
                    ? t(
                        `Staff.salaryType${props.selectedEmployment.salaryType}`,
                      )
                    : "-"}
                </FieldValue>
                <FieldLabel>
                  {t("Staff.AddStaffPanelFieldSalaryModel")}
                </FieldLabel>
              </FieldCard>
              <FieldCard>
                <FieldValue>
                  {props.selectedEmployment?.salary == null
                    ? "-"
                    : (() => {
                        const displayValue = String(
                          props.selectedEmployment.salary,
                        ).trim();
                        return displayValue.length > 0
                          ? `${displayValue} kr`
                          : "-";
                      })()}
                </FieldValue>
                <FieldLabel>
                  {t("Staff.AddStaffPanelFieldSalaryAmount")}
                </FieldLabel>
              </FieldCard>
              {shouldShowPayoutSalary && payoutSalary != null && (
                <FieldCard>
                  <FieldValue>{formatMonthlySalary(payoutSalary)}</FieldValue>
                  <FieldLabel>{t("Salary.PayoutSalary")}</FieldLabel>
                </FieldCard>
              )}
              <FieldCard>
                <FieldValue>
                  {props.selectedEmployment?.paymentType
                    ? props.selectedEmployment.paymentType === "None"
                      ? t("System.OwnSalaryManagement")
                      : "SimflexityPay"
                    : "-"}
                </FieldValue>
                <FieldLabel>
                  {t("Staff.AddStaffPanelFieldPaymentSolution")}
                </FieldLabel>
              </FieldCard>
              {vacationVisibility.showVacationPayoutType && (
                <FieldCard>
                  <FieldValue>{selectedVacationPayoutLabel}</FieldValue>
                  <FieldLabel>
                    {t("Staff.AddStaffPanelFieldVacationPayoutType")}
                  </FieldLabel>
                </FieldCard>
              )}
            </SalaryFieldsGrid>
            {!props.isEditMode && (
              <SectionActionRow>
                <SecondaryButton
                  label={t("Staff.ProfilePanelChangeSalary")}
                  size="small"
                  onClick={props.onChangeSalaryClick}
                />
              </SectionActionRow>
            )}
          </SectionBox>
        </Section>
      )}

      {(!props.isEditMode || fieldVisibility.showTaxTable) && (
        <Section>
          <SectionTitle>{t("Salary.TaxProcent")}</SectionTitle>
          <SectionBox>
            <TwoColumnFieldsGrid>
              <FieldCard>
                <>
                  <FieldValue>
                    {props.selectedEmployment?.taxTable ?? "-"}
                  </FieldValue>
                  <FieldLabel>
                    {t("Staff.AddStaffPanelFieldTaxTable")}
                  </FieldLabel>
                </>
              </FieldCard>
            </TwoColumnFieldsGrid>
            {!props.isEditMode && (
              <SectionActionRow>
                <SecondaryButton
                  label={t("Staff.ProfilePanelChangeTax")}
                  size="small"
                  onClick={props.onChangeTaxClick}
                />
              </SectionActionRow>
            )}
          </SectionBox>
        </Section>
      )}

      <Section>
        <SectionTitle>{t("Staff.PaymentMethodSectionTitle")}</SectionTitle>
        <SectionBox>
          <TwoColumnFieldsGrid>
            <FieldCard>
              <>
                <FieldValue>{paymentMethod?.value?.trim() || "-"}</FieldValue>
                <FieldLabel>{t("Staff.PaymentMethodValue")}</FieldLabel>
              </>
            </FieldCard>
            <FieldCard>
              <>
                <FieldValue>
                  {paymentMethod?.type
                    ? t(`Staff.PaymentMethod${paymentMethod.type}`)
                    : "-"}
                </FieldValue>
                <FieldLabel>{t("Staff.PaymentMethodType")}</FieldLabel>
              </>
            </FieldCard>
          </TwoColumnFieldsGrid>
          {!props.isEditMode && (
            <SectionActionRow>
              <SecondaryButton
                label={t("Staff.ProfilePanelChangePaymentMethod")}
                size="small"
                onClick={props.onChangePaymentMethodClick}
              />
            </SectionActionRow>
          )}
        </SectionBox>
      </Section>

      {(!props.isEditMode || fieldVisibility.showContractTerms) && (
        <Section>
          <SectionTitle>{t("Staff.ProfilePanelContractTerms")}</SectionTitle>
          <SectionBox>
            <TwoColumnFieldsGrid>
              <FieldCard>
                {props.isEditMode ? (
                  <InputField
                    fullWidth
                    label={t(
                      "Staff.AddStaffPanelFieldCollectiveSalaryAgreement",
                    )}
                    value={props.editedEmploymentDetails.agreement}
                    hasError={validationErrors.agreement != null}
                    errorText={
                      validationErrors.agreement == null
                        ? undefined
                        : t(validationErrors.agreement.translationKey)
                    }
                    onChange={(agreement) =>
                      props.onEmploymentFieldChange("agreement", agreement)
                    }
                  />
                ) : (
                  <>
                    <FieldValue>
                      {props.selectedEmployment?.agreement?.trim() || "-"}
                    </FieldValue>
                    <FieldLabel>
                      {t("Staff.AddStaffPanelFieldCollectiveSalaryAgreement")}
                    </FieldLabel>
                  </>
                )}
              </FieldCard>
              <FieldCard>
                {props.isEditMode ? (
                  <InputField
                    fullWidth
                    type="number"
                    label={t("Staff.AddStaffPanelFieldNoticePeriodDays")}
                    value={props.editedEmploymentDetails.noticeDays}
                    hasError={validationErrors.noticeDays != null}
                    errorText={
                      validationErrors.noticeDays == null
                        ? undefined
                        : t(validationErrors.noticeDays.translationKey)
                    }
                    onChange={noticeDaysHandlers.onChange}
                    onBlur={noticeDaysHandlers.onBlur}
                  />
                ) : (
                  <>
                    <FieldValue>
                      {props.selectedEmployment?.noticeDays ?? "-"}
                    </FieldValue>
                    <FieldLabel>
                      {t("Staff.AddStaffPanelFieldNoticePeriodDays")}
                    </FieldLabel>
                  </>
                )}
              </FieldCard>
              <FieldCard>
                {props.isEditMode ? (
                  <InputField
                    fullWidth
                    type="number"
                    label={t("Staff.AddStaffPanelFieldVacationDays")}
                    value={props.editedEmploymentDetails.vacationDays}
                    hasError={validationErrors.vacationDays != null}
                    errorText={
                      validationErrors.vacationDays == null
                        ? undefined
                        : t(validationErrors.vacationDays.translationKey)
                    }
                    onChange={vacationDaysHandlers.onChange}
                    onBlur={vacationDaysHandlers.onBlur}
                  />
                ) : (
                  <>
                    <FieldValue>
                      {props.selectedEmployment?.vacationDays ?? "-"}
                    </FieldValue>
                    <FieldLabel>
                      {t("Staff.AddStaffPanelFieldVacationDays")}
                    </FieldLabel>
                  </>
                )}
              </FieldCard>
              <FieldCard>
                {props.isEditMode ? (
                  <InputField
                    fullWidth
                    label={t("Staff.AddStaffPanelFieldWorkPlace")}
                    value={props.editedEmploymentDetails.workplace}
                    hasError={validationErrors.workplace != null}
                    errorText={
                      validationErrors.workplace == null
                        ? undefined
                        : t(validationErrors.workplace.translationKey)
                    }
                    onChange={(workplace) =>
                      props.onEmploymentFieldChange("workplace", workplace)
                    }
                  />
                ) : (
                  <>
                    <FieldValue>
                      {props.selectedEmployment?.workplace?.trim() || "-"}
                    </FieldValue>
                    <FieldLabel>
                      {t("Staff.AddStaffPanelFieldWorkPlace")}
                    </FieldLabel>
                  </>
                )}
              </FieldCard>
            </TwoColumnFieldsGrid>
          </SectionBox>
        </Section>
      )}

      {vacationVisibility.showVacationEarningDays && (
        <Section>
          <SectionTitle>{t("Staff.AddStaffPanelSectionVacation")}</SectionTitle>
          <SectionBox>
            <TwoColumnFieldsGrid>
              <FieldCard>
                <FieldValue>
                  {props.selectedEmployment?.maxSickLeaveDays ?? "-"}
                </FieldValue>
                <FieldLabel>
                  {t("Staff.AddStaffPanelFieldMaxSickLeaveDays")}
                </FieldLabel>
              </FieldCard>
              <FieldCard>
                <FieldValue>
                  {props.selectedEmployment?.maxCareOfChildDays ?? "-"}
                </FieldValue>
                <FieldLabel>
                  {t("Staff.AddStaffPanelFieldMaxCareOfChildDays")}
                </FieldLabel>
              </FieldCard>
              <FieldCard>
                <FieldValue>
                  {props.selectedEmployment?.maxParentalLeaveDays ?? "-"}
                </FieldValue>
                <FieldLabel>
                  {t("Staff.AddStaffPanelFieldMaxParentalLeaveDays")}
                </FieldLabel>
              </FieldCard>
            </TwoColumnFieldsGrid>
          </SectionBox>
        </Section>
      )}
    </>
  );
};
